import React, { useEffect, useState } from 'react';
import { Modal, message } from 'antd';
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';
import { CancelIcon, DeleteIcon } from '../../../components/Icons/Icons';
import { Button } from '../../../components/buttons/buttons';
import { massDeleteTasks } from '../../../Api/Services/TaskapiService'
import { massDeleteLeads } from '../../../Api/Services/LeadApiServices'
import { massDeleteAccount } from '../../../Api/Services/AccountApiService'
import { massDeleteContact } from '../../../Api/Services/ContactApiService'
import { massDeleteDeals } from '../../../Api/Services/DealApiService'
import { deleteCase } from '../../../Api/Services/CaseApiServices'

const Popup = ({
    confirmPopup,
    setConfirmPopup,
    Respone,
    page,
    type, onMergeclick,
    getCallBack, DeletePermission
}) => {
    const [linkedErrors, setLinkedErrors] = useState([])
    const [errorData, setErrorData] = useState([])
    const [taskOnlyRecords, setTaskOnlyRecords] = useState([])
    const [deleting, setDeleting] = useState(false)
    const handleClose = () => {
        setConfirmPopup(false)
        getCallBack()
    }
    const getEntityDisplayValues = (item) => {
        if (!item?.entityData || !Array.isArray(item.entityData)) return [];
        if (item.entity === 'Leads') return item.entityData.map(lead => `${lead.firstName} ${lead.lastName}`);
        if (item.entity === 'Contacts') return item.entityData.map(contact => `${contact.firstName} ${contact.lastName}`);
        if (item.entity === 'Accounts') return item.entityData.map(account => account.accountName);
        if (item.entity === 'Deals') return item.entityData.map(deal => deal.dealName);
        if (item.entity === 'Tasks') return item.entityData.map(task => task.taskName);
        if (item.entity === 'Quotes') return item.entityData.map(quote => quote.quotesName);
        if (item.entity === 'SalesOrders') return item.entityData.map(salesOrder => salesOrder.salesOrderName);
        if (item.entity === 'Invoices') return item.entityData.map(invoice => invoice.invoiceName);
        if (item.entity === 'Cases') return item.entityData.map(caseItem => caseItem.caseSubject);
        if (item.entity === 'UserGroups') return item.entityData.map(group => group.userGroupName);
        if (item.entity === 'Territories') return item.entityData.map(territory => territory.territoryName);
        if (item.entity === 'Campaign') return item.entityData.map(campaign => campaign.campaignName);
        if (item.entity === 'ServiceLines') return item.entityData.map(serviceLine => serviceLine.serviceLineName);
        if (item.entity === 'Users') return item.entityData.map(user => user.userName);
        if (item.entity === 'Company') return ['Company Insights'];
        return [];
    };
    const getTaskOnlyRecords = (data) => {
        if (!Array.isArray(data)) return [];
        const groupedByEntityId = data.reduce((acc, item) => {
            const entityId = item?.entityId;
            if (entityId === undefined || entityId === null) return acc;
            if (!acc[entityId]) acc[entityId] = [];
            acc[entityId].push(item);
            return acc;
        }, {});
        return Object.values(groupedByEntityId)
            .filter(records => records.length > 0 && records.every(record => record?.entity === 'Tasks'))
            .map(records => ({
                entityId: records[0].entityId,
                entityName: records[0].entityName,
                entity: 'Tasks',
                entityData: records.flatMap(record => record.entityData || [])
            }));
    };
    const getDeleteApiAndPayload = (type, entityIds) => {
        switch (type) {
            case "Lead":
                return {
                    api: massDeleteLeads,
                    payload: {
                        leadIds: entityIds
                    }
                };

            case "Account":
                return {
                    api: massDeleteAccount,
                    payload: {
                        accountIds: entityIds
                    }
                };

            case "Contact":
                return {
                    api: massDeleteContact,
                    payload: {
                        contactIds: entityIds
                    }
                };

            case "Deal":
                return {
                    api: massDeleteDeals,
                    payload: {
                        dealIds: entityIds
                    }
                };
            case "Case":
                return {
                    api: deleteCase,
                    payload: entityIds[0]
                };
            default:
                return null;
        }
    };
    const handleDeleteLinkedTasks = async () => {
        if (!taskOnlyRecords.length) return;
        try {
            setDeleting(true);
            const entityIds = [...new Set(taskOnlyRecords.map(item => Number(item.entityId)).filter(Boolean))];
            const taskId = [...new Set(taskOnlyRecords.flatMap(item => item.entityData?.map(task => Number(task.taskId)) || []))];
            const payload = { taskIds: taskId };
            const taskResponse = await massDeleteTasks(payload);
            if (!taskResponse) { throw new Error("Task deletion failed.Try again"); }
            if (DeletePermission !== "Yes") {
                const deleteConfig = getDeleteApiAndPayload(type, entityIds);
                const response = await deleteConfig.api(deleteConfig.payload);
                if (!response) { throw new Error(`${type} deletion failed.Try again`); }
            }
            else {
                onMergeclick()
            }
            setConfirmPopup(false);
            getCallBack();
        } catch (error) {
            message.error(error?.message);
        } finally {
            setDeleting(false);
        }
    };
    useEffect(() => {
        if (page === "Details") {
            const successData = Respone.filter(item => item.entityType === 'Success');
            const groupedData = successData.reduce((acc, item) => {
                if (!acc[item.entityMessage]) acc[item.entityMessage] = [];
                acc[item.entityMessage].push(item.entityName);
                return acc;
            }, {});
            const sortedEntries = Object.entries(groupedData).sort(([a], [b]) => {
                if (a.includes("Deleted Successfully")) return -1;
                if (b.includes("Deleted Successfully")) return 1;
                return 0;
            });
            setErrorData(sortedEntries)
            const nonSuccessData = Respone.filter(item => item.entityType !== 'Success');
            const taskOnly = type === "User" ? [] : getTaskOnlyRecords(nonSuccessData);
            setTaskOnlyRecords(taskOnly);
            const normalErrorData = type === "User" ? nonSuccessData : nonSuccessData.filter(item => {
                return item?.entity !== 'Tasks';
            });
            setLinkedErrors(normalErrorData);
        }
        if (page === "DetailsError") {
            const successData = Respone.filter(item => item.entityType === 'Success');
            const groupedData = successData.reduce((acc, item) => {
                if (!acc[item.entityMessage]) acc[item.entityMessage] = [];
                acc[item.entityMessage].push(item.entityName);
                return acc;
            }, {});
            const sortedEntries = Object.entries(groupedData).sort(([a], [b]) => {
                if (a.includes("Deleted Successfully")) return -1;
                if (b.includes("Deleted Successfully")) return 1;
                return 0;
            });
            setErrorData(sortedEntries)
            const nonSuccessData = Respone.filter(item => item.entityType !== 'Success');
            const taskOnly = type === "User" ? [] : getTaskOnlyRecords(nonSuccessData);
            setTaskOnlyRecords(taskOnly);
            const normalErrorData = type === "User" ? nonSuccessData : nonSuccessData.filter(item => {
                return item?.entity !== 'Tasks';
            });
            const groupedArray = Object.values(
                normalErrorData.reduce((acc, curr) => {
                    const { entityName, entity, entityData } = curr;
                    if (!acc[entityName]) {
                        acc[entityName] = {
                            entityName,
                            data: []
                        };
                    }
                    acc[entityName].data.push({ entity, entityData });
                    return acc;
                }, {})
            );
            setLinkedErrors(groupedArray)
        }
    }, [Respone, page, type])
    return (
        <>
            <Modal
                title="Linked records?"
                open={confirmPopup}
                onCancel={handleClose}
                maskClosable={false}
                footer={(DeletePermission === "Yes" && linkedErrors.length > 0) ? false :
                    taskOnlyRecords.length > 0 ? [
                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                            <Button onClick={handleClose} className="gray-button" size="small" disabled={deleting}>
                                <CancelIcon />Cancel
                            </Button>
                            <Button htmlType="submit" size="small" onClick={handleDeleteLinkedTasks} className='red-button' loading={deleting} disabled={deleting}>
                                {!deleting && <DeleteIcon />} {deleting ? 'Deleting...' : 'Delete'}
                            </Button>
                        </div>
                    ] : false
                }
            >
                <div  >
                    {page === "Details" ?
                        <>
                            {errorData.length > 0 && errorData.map(([message, names]) => (
                                <div key={message} style={{ marginBottom: 12 }}>
                                    <div style={{ marginBottom: 5, textAlign: "left" }}>{message?.includes("Deleted Successfully") ? <CheckCircleOutlined style={{ color: "green" }} /> : <ExclamationCircleOutlined style={{ color: '#f28e42' }} />} {message?.includes("Deleted Successfully") ? `Following ${type.toLowerCase()}s have been deleted successfully` : message}</div>
                                    <div style={{ paddingLeft: 20, wordBreak: 'break-word', textAlign: "left", fontWeight: 600 }}>
                                        {names.map((name, index) => (
                                            <span key={name}>
                                                {name}
                                                {index < names.length - 1 ? ', ' : ''}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            {linkedErrors.length > 0 ? <>
                                <div className='d-flex align-items-start justify-content-center' style={{ fontSize: 15, marginBottom: 10 }}>
                                    <p><ExclamationCircleOutlined style={{ color: '#f28e42' }} /> This record cannot be deleted because it has linked records. Please unassign or delete those linked records before retrying.</p>
                                </div>
                                <div>
                                    {linkedErrors.map((item, index) => {
                                        const displayValues = getEntityDisplayValues(item);
                                        return (
                                            <div key={index}>
                                                Linked {item.entity} : <strong>{displayValues.join(', ')}</strong>
                                            </div>
                                        );
                                    })}
                                </div></> : <></>}
                        </>
                        :
                        page === "DetailsError" ?
                            <>
                                {errorData.length > 0 && errorData.map(([message, names]) => (
                                    <div key={message} style={{ marginBottom: 12 }}>
                                        <div style={{ marginBottom: 5, textAlign: "left" }}>{message?.includes("Deleted Successfully")
                                            ? <CheckCircleOutlined style={{ color: "green" }} />
                                            : <ExclamationCircleOutlined style={{ color: '#f28e42' }} />} {message?.includes("Deleted Successfully") ? `Following ${type.toLowerCase()}s have been deleted successfully` : message}</div>
                                        <div style={{ paddingLeft: 20, wordBreak: 'break-word', textAlign: "left", fontWeight: 600 }}>
                                            {names.map((name, index) => (
                                                <span key={name}>
                                                    {name}
                                                    {index < names.length - 1 ? ', ' : ''}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                                {linkedErrors.length > 0 ? <>
                                    <div className='d-flex align-items-start justify-content-center' style={{ fontSize: 15, marginBottom: 10 }}>
                                        <p><ExclamationCircleOutlined style={{ color: '#f28e42' }} /> Following record(s) cannot be deleted because it has linked records. Please unassign or delete those linked records before retrying.</p>
                                    </div>
                                    <div>
                                        {linkedErrors.map((record, index) => (
                                            <div key={index} style={{ marginBottom: '5px' }}>
                                                Record Name : <strong>{record.entityName}</strong>
                                                <ul style={{ marginTop: '3px' }}>
                                                    {record.data.map((item, idx) => (
                                                        <li key={idx}>
                                                            Linked {item.entity} : <strong>{item.entityData.length}</strong>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                </> : <></>}
                            </> : ""
                    }
                    {(DeletePermission === "Yes" && linkedErrors.length > 0) ? <></> :
                        (taskOnlyRecords.length > 0 && (page === "Details" || page === "DetailsError")) && (
                            <div style={{ marginTop: linkedErrors.length > 0 ? 20 : 0, paddingTop: linkedErrors.length > 0 ? 15 : 0, borderTop: linkedErrors.length > 0 ? '1px solid #ddd' : 'none' }}>
                                <div className='d-flex align-items-start justify-content-center' style={{ fontSize: 15, marginBottom: 10 }}>
                                    <p><ExclamationCircleOutlined style={{ color: '#f28e42' }} />{page === "Details" ? " This record" : " Following record(s)"} have linked tasks and can be deleted after confirmation. Deleting these records will also delete their linked task</p>
                                </div>
                                <div >
                                    {page === "Details" ? (
                                        taskOnlyRecords.map((record, index) => {
                                            const taskNames = getEntityDisplayValues(record);
                                            return (
                                                <div key={index}>
                                                    Linked {record.entity} : <strong>{taskNames.join(', ')}</strong>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        taskOnlyRecords.map((record, index) => (
                                            <div key={index} style={{ marginBottom: '5px' }}>
                                                Record Name : <strong>{record.entityName}</strong>
                                                <ul style={{ marginTop: '3px' }}>
                                                    <li>
                                                        Linked {record.entity} : <strong>{record.entityData.length}</strong>
                                                    </li>
                                                </ul>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        )}
                </div>
            </Modal >
        </>
    );
};

Popup.propTypes = {
    confirmPopup: PropTypes.bool.isRequired,
    setConfirmPopup: PropTypes.func.isRequired,
    Respone: PropTypes.array.isRequired,
    page: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    getCallBack: PropTypes.func.isRequired,
    DeletePermission: PropTypes.string.isRequired,
    onMergeclick: PropTypes.func.isRequired,
};

export default Popup;